<script lang="ts" setup>
	import { useResourcesApi } from '~/services/apis/resources'
	import StringMethods from '~/utils/character'

	interface Props {
		productId: string
		showAction?: boolean
		showCount?: boolean
		selectedHospitals?: HospitalListSchema
	}

	const { showCount = true, ...props } = defineProps<Props>()

	const { getHospitalList, getStatesWithLGA } = useResourcesApi()
	const { StatesWithLga } = reactor(useUser())
	const { hospitalTypeIcon, hospitalTypes, getHospitalType } = useStore()
	const { isMobile } = useMobile()
	const { format } = useCurrency()
	const { toCapitalize } = StringMethods()

	const selectedCategory = defineModel('category', { default: '' })
	const search = defineModel('search', { default: '' })
	const filterByState = defineModel('state', { default: 'all' })

	const selectedHospital = defineModel<HospitalListSchema>('selectedHospital')

	const { data, getOptions, paginationFilter } = useScrollPaginator<HospitalListSchema>({
		fetchOptions: fetchHospitalList,
		elementId: '#hospital-list',
		filterKey: 'id',
		watch: computed(() => [search.value, filterByState.value, selectedCategory.value, props.productId])
	})

	const categoryLabel = computed(() => getHospitalType(selectedCategory.value))

	const { status } = useLazyAsyncData(
		'hospital-states',
		async () => await Promise.allSettled([getStatesWithLGA(), getOptions()]),
		useApiCache()
	)

	const statesList = computed(() => [{ label: 'All States', value: '' }, ...StatesWithLga.value])

	async function fetchHospitalList(arg?: RecordSchema) {
		if (props.productId) {
			const result = await getHospitalList({
				page: 1,
				...(arg ? arg : {}),
				search: search.value,
				location: filterByState.value === 'all' ? '' : filterByState.value,
				product_id: String(props.productId),
				type: categoryLabel.value?.value === 'all' ? '' : categoryLabel.value?.value
			})
			return result.value?.data || null
		}
		useNotification().alert({
			status: 400,
			message: 'Product is required to fetch hospital list'
		})
		return null
	}

	const pickCategory = (param: string) => {
		selectedCategory.value = param.toLowerCase()
	}

	const selectHospital = (param: HospitalListSchema) => {
		selectedHospital.value = param
	}

	watch(status, newStatus => {
		if (newStatus === 'success' && !isMobile.value) selectedHospital.value = data.value[0]
	})
</script>

<template>
	<div class="relative">
		<slot name="filter" :types="hospitalTypes" :states="statesList">
			<div
				data-id="hospital-categories"
				class="transparent-scroll flex items-center gap-3 overflow-hidden overflow-x-auto"
			>
				<button
					v-for="(item, index) in hospitalTypes"
					:key="index"
					type="button"
					name="hospital-category"
					class="inline-flex min-h-[61.87px] min-w-[70px] flex-none cursor-pointer flex-col items-center justify-center gap-[7.63px] border bg-white p-[6.36px]"
					:class="[selectedCategory === item.value ? 'border-violet-500' : 'border-gray-200']"
					@click="pickCategory(item.value)"
				>
					<div
						class="flex h-[30.524px] w-[30.524px] flex-none items-center justify-center rounded-full bg-gray-100"
					>
						<tools-icon dir="hospital-tools" :icon="item.icon" />
					</div>

					<span
						class="max-w-[60px] overflow-hidden text-ellipsis whitespace-nowrap text-center text-[11px] font-semibold text-gray-600"
						:title="item.label"
					>
						{{ item.label }}
					</span>
				</button>
			</div>
		</slot>

		<slot name="search">
			<fieldset class="relative my-8 w-full">
				<div
					class="sticky top-0 z-10 flex h-[40px] w-full items-center overflow-hidden rounded-[66px] border border-gray-200 bg-white focus-within:border-purple-500 hover:border-purple-500"
				>
					<forms-search-field
						v-model:value="search"
						:use-custom-class="false"
						input-class="flex-none !pt-1 !pl-0 pr-10 !border !border-gray-300"
						class="w-full pl-5"
					/>

					<a-select
						v-model:value="filterByState"
						class="border- filter-hospitals ml-auto w-full max-w-[110px] !bg-transparent sm:max-w-[130px]"
						placeholder="All states"
						:bordered="false"
						:options="statesList"
					/>
				</div>
			</fieldset>
		</slot>

		<div v-if="showCount" class="mb-3 mt-10 text-sm">
			<span class="font-semibold capitalize text-gray-600">
				{{ categoryLabel?.label || '---' }}
			</span>
			({{ format(paginationFilter.total_count) }})
		</div>

		<a-spin class="space-y-5" wrapperClassName="space-y-5 min-h-[305px]" :spinning="paginationFilter.loading">
			<ul
				class="mb-5 h-full max-h-[500px] min-h-[100px] scroll-pr-5 overflow-y-auto lg:max-h-[300px]"
				id="hospital-list"
			>
				<template v-if="data.length > 0">
					<template v-for="item in data" :key="item.id">
						<slot name="hospital" :hospital="item">
							<li
								class="flex items-center justify-between gap-2 border-b border-gray-200 !px-0 py-2 last:border-b-0"
							>
								<div class="flex items-center gap-3">
									<div
										class="border-primary- flex h-[48px] w-[48px] flex-none items-center justify-center rounded-full border bg-purple-50"
										:class="{
											'border-primary-2': selectedHospital?.name === item.name
										}"
									>
										<tools-icon
											dir="hospital-tools"
											:icon="hospitalTypeIcon[item.type] || 'clinic'"
										/>
									</div>

									<div class="inline-flex flex-col justify-start gap-1">
										<div class="text-sm font-semibold capitalize text-gray-700">
											{{ item.name }}
										</div>
										<div
											class="max-w-[500px] text-ellipsis text-pretty text-xs font-normal text-gray-500"
										>
											{{ toCapitalize(item.address) }},
											{{ toCapitalize(item.location) }}
										</div>
									</div>
								</div>
								<button
									v-if="showAction"
									class="text-sm font-semibold text-primary-2"
									type="button"
									@click="selectHospital(item)"
								>
									Visit
								</button>
							</li>
						</slot>
					</template>
				</template>
				<template v-else-if="!paginationFilter.loading">
					<shared-empty-state
						:subtitle="`No ${!categoryLabel?.value ? 'Hospital' : categoryLabel?.label || ''} found`"
					/>
				</template>
			</ul>
		</a-spin>
	</div>
</template>

<style>
	.filter-hospitals.ant-select:not(.ant-select-customize-input) .ant-select-selector {
		@apply !bg-transparent;
	}
</style>
